/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import ErrorResult from "../../Commands/ErrorResult";
import { fixedT, translationKeys } from "./../../Internationalisierung/i18nBasis";
import AppSnackbarItem, { Code } from "./AppSnackbarItem";

type AppSnackbarItemOptions = Partial<AppSnackbarItem>;

export default class AppSnackbarItemFactory {
    public create(options: AppSnackbarItemOptions): AppSnackbarItem {
        return {
            source: options.source ?? "",
            message: options.message ?? "",
            code: options.code ?? "",
            level: options.level ?? "Success",
            path: options.path ?? [],
            referenceId: options.referenceId ?? "",
            referenceMatchcode: options.referenceMatchcode ?? "",
            referenceTypeName: options.referenceTypeName ?? "",
            errorId: options.errorId ?? "",
            action: options.action,
        };
    }

    public async createFromResponse(requestName: string | undefined, response: Response): Promise<AppSnackbarItem[]> {
        const fehlerText = await response.text();
        const errorResult: ErrorResult | null = fehlerText ? JSON.parse(fehlerText) : null;
        if (errorResult && errorResult.errors) {
            return this.createFromErrorResult(requestName, errorResult);
        }

        return [createInternalErrorItem("")];
    }

    public createFromErrorResult(requestName: string | undefined, errorResult: ErrorResult): AppSnackbarItem[] {
        return errorResult.errors?.map(error => new AppSnackbarItemFactory().create({
            source: requestName,
            message: error.message,
            level: "Error",
            path: [
                ...(typeof requestName === "undefined" ? [] : [requestName]),
                ...(error.path?.map(p => p.toString()) ?? [])
            ],
            action: error.action,
        })) ?? [];
    }
}

export const createSuccessItem = (source: string): AppSnackbarItem =>
    new AppSnackbarItemFactory().create({
        source,
        message: fixedT(translationKeys.appSnackbarItemFactory.erfolgreich),
        level: "Success",
    });

export const createInternalErrorItem = (source?: string): AppSnackbarItem =>
    new AppSnackbarItemFactory().create({
        source,
        code: "EXEC_INTERNAL_ERROR",
        level: "Error",
    });

export const createNetworkErrorItem = (source?: string): AppSnackbarItem =>
    new AppSnackbarItemFactory().create({
        source,
        message: fixedT(translationKeys.cloudApolloErrorHandler.networkError),
        level: "Error",
    });

export const createCustomErrorItem = (source: string, message: string, code?: Code, path?: string[]): AppSnackbarItem =>
    new AppSnackbarItemFactory().create({
        source,
        message: message,
        level: "Error",
        code,
        path
    });

export const createPdfErrorItem = (source?: string): AppSnackbarItem =>
    new AppSnackbarItemFactory().create({
        source,
        code: "EXEC_PDF_ERROR",
        level: "Error",
        message: fixedT(translationKeys.appSnackbarItemFactory.pdfError),
    });
